import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, useLocation } from "react-router-dom";
import { ThunkDispatch } from 'redux-thunk';
import { ca } from '@cian/analytics';
import { TDeviceType, useDeviceType } from "@cian/ui-kit";

import { IUser } from '../../types/user';
import { IJournal } from '../../types/journal';
import { IGlobalErrorState } from '../../types/global_error';
import { getJournal } from '../../reducers/modules/journal/journal';
import { List } from '../../components/list/list';
import { LoadError } from '../../components/load_error/load_error';
import { SelectedPosts } from '../selected_posts/selected_posts';
import { Aside } from '../aside/aside';
import { BreadCrumbs } from '../bread_crumbs/bread_crumbs';
import { setBreadCrumb } from '../../reducers/modules/bread_crumbs/bread_crumbs';
import { IBreadItem } from '../../types/bread_crumb';
import { getSelectedPosts } from '../../reducers/modules/selected_posts/selected_posts';
import { IAdvertising } from '../../types/advertising';
import { saveScroll } from '../../reducers/modules/scroll/scroll';
import { IScroll } from '../../types/scroll';
import { getDiscussed } from '../../reducers/modules/discussed/discussed';
import { JournalListItem } from '../../components/list_item/journal_list_item';
import { refreshScroll } from '../../reducers/modules/scroll/scroll';
import { scrollComments } from '../../reducers/modules/scroll_comment/scroll_comment';
import { IConfig } from '../../types/config';
import { combineDataLayerObject } from '../../../app/helpers/combine_data_layer_object';
import { getBreadCrumbsNames } from '../../../app/helpers/breadcrumbs_helper';
import { IApplicationState, TReduxActions, IThunkContext } from "../../types/redux";
import { IAnalytics } from "../../types/analytics";

import s from './journal.css';

interface IProps {
  user: IUser;
  journal: IJournal;
  advertising: IAdvertising;
  scroll: IScroll;
  globalError: IGlobalErrorState;
  config: IConfig;
  deviceType: TDeviceType;
  analytics: IAnalytics;
}

interface IDispatchProps {
  getJournal(page: number, ordering: string, tag: string, search: string, regionID: string): void;
  getSelectedPosts(regionID: string): void;
  setBreadCrumb(path: IBreadItem[]): void;
  getDiscussed(regionID: string): void;
  saveScroll(item: any): void;
  refreshScroll(): void;
  scrollComments(): void;
}

export const setCrumbsJournal = (tag: string, search: string, category: string) => {
  const crumbs: IBreadItem[] = [
    { path: '/magazine' },
  ];
  if (!!tag) {
    const name = tag[0].toUpperCase() + tag.substr(1);

    crumbs.push({
      path: tag,
      url: `/magazine?tag=${tag}`,
      name,
    });
  } else if (!!search) {
    crumbs.push({
      path: search,
      url: `/magazine?search=${search}`,
      name: 'Поиск',
    });
  } else if (!!category) {
    crumbs.push({ path: category });
  }

  return crumbs;
}

const JournalContainer: React.FC<IProps & IDispatchProps & RouteComponentProps> = props => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const tag = query.get("tag") || '';
  const search = query.get("search") || '';
  const ordering = query.get("ordering") || '-date_publish';

  const { config } = props;
  const deviceType = useDeviceType();
  const page = query.get("page") ? parseInt(query.get("page") as string) : 1;
  const regionID = config.regionID && config.regionID !== '0' ? config.regionID : '';
  const category = query.get("category") || '';

  React.useEffect(() => {
    const crumbs = setCrumbsJournal(tag, search, category);

    if (typeof window !== 'undefined') {
      ca('pageviewSite', combineDataLayerObject(props.analytics, getBreadCrumbsNames(crumbs), deviceType));
    }

    props.getJournal(page, ordering, tag, search, regionID);
    props.getSelectedPosts(regionID);
    props.setBreadCrumb(crumbs);
    props.getDiscussed(regionID);
  }, [tag, search, ordering, page, regionID, category]);

  const {
    journal: {
      items: journalItems,
      pages_count: pagesCount,
      count: recordsCount,
    },
    scroll: { item: selectedItem },
  } = props;

  const loadMore = (pageN: number) => {
    const page = pageN || 1;
    const regionID = props.config.regionID && props.config.regionID !== '0' ? props.config.regionID : '';

    return props.getJournal(page, ordering, tag, search, regionID);
  }

  const seoHideBlock = () => {
    return (
      <span className={s['journal__schema']}>
        <span itemProp="headline">ЦИАН - Новости, статьи и аналитика рынка недвижимости в Москве и России</span>
        <span itemProp="description">
          ЦИАН - новости, статьи, аналитика и другая информация о рынке недвижимости в Москве и России.
        </span>
      </span>
    );
  }

  return (
    <div>
      { (query.get("tag") || query.get("search")) ?
        <div className={s['journal-tag']}/> :
        <div>
          <SelectedPosts />
        </div>
      }
      <div className={s['journal-wrap']}>
        <div itemScope itemType="https://schema.org/Article" className={`${s['journal']} cg-col-16 cg-col-xs-16`}>
          {seoHideBlock()}
          { (query.get("tag") || query.get("search")) && <BreadCrumbs /> }
          {
            props.globalError.error ?
              <LoadError /> :
              <List
                currentUser={props.user}
                items={journalItems}
                pagesCount={pagesCount}
                recordsCount={recordsCount}
                searchTag={tag}
                saveScroll={props.saveScroll}
                selectedItem={selectedItem}
                listItemClass={JournalListItem}
                refreshScroll={props.refreshScroll}
                scrollComments={props.scrollComments}
                search={search}
                onLoadMore={(page: number) => loadMore(page)} />
          }
        </div>
        <Aside />
      </div>
    </div>
  );
}

function mapStateToProps(state: IApplicationState): IProps {
  const {
    journal,
    advertising,
    scroll,
    globalError,
    user,
    config,
    deviceType,
    analytics,
  } = state;

  return {
    user,
    journal,
    advertising,
    scroll,
    globalError,
    config,
    deviceType,
    analytics,
  };
}

function mapDispatchToProps(dispatch: ThunkDispatch<IApplicationState, IThunkContext, TReduxActions>): IDispatchProps {
  return {
    getJournal: (page: number, ordering: string, tag: string, search: string, regionID: string) => {
      dispatch(getJournal(page, ordering, tag, search, regionID));
    },
    getSelectedPosts: (regionID: string) => {
      dispatch(getSelectedPosts(regionID));
    },
    setBreadCrumb: (path: IBreadItem[]) => {
      dispatch(setBreadCrumb(path));
    },
    getDiscussed: (regionID: string) => {
      dispatch(getDiscussed(regionID));
    },
    saveScroll: (item: any) => {
      dispatch(saveScroll(item));
    },
    refreshScroll: () => {
      dispatch(refreshScroll());
    },
    scrollComments: () => {
      dispatch(scrollComments());
    },
  }
}

export const Journal = connect(mapStateToProps, mapDispatchToProps)(JournalContainer);
